import { useCallback, useEffect, useRef } from "react";

import {
  useAnnotationTool,
  type AnnotationTool,
  type EraseMode,
} from "@/stores/annotation-tool";

import type { EditorOpts, PageEditor } from "./fabric-editor";
import type { SmoothingPreset } from "./ink";

/** Every field of the live tool that a `PageEditor` mirrors. */
export interface AnnotationStyle {
  tool: AnnotationTool;
  color: string;
  fontFamily: string;
  fontSizeRel: number;
  strokeRel: number;
  highlighterRel: number;
  eraserRel: number;
  eraseMode: EraseMode;
  pressure: boolean;
  smoothing: SmoothingPreset;
  shapeFill: string | null;
  shapeDash: boolean;
  shapeCorner: number;
}

/** Subscribe to the live tool and its style, one selector per field.
 *
 * All three host surfaces read the same set here. They used to keep three copies
 * of the list, and the copies drifted: `PdfViewer` omitted the shape style, so a
 * fill, dash or corner change never reached a PDF page (WE-08). */
export function useAnnotationStyle(): AnnotationStyle {
  return {
    tool: useAnnotationTool((s) => s.tool),
    color: useAnnotationTool((s) => s.color),
    fontFamily: useAnnotationTool((s) => s.fontFamily),
    fontSizeRel: useAnnotationTool((s) => s.fontSizeRel),
    strokeRel: useAnnotationTool((s) => s.strokeRel),
    highlighterRel: useAnnotationTool((s) => s.highlighterRel),
    eraserRel: useAnnotationTool((s) => s.eraserRel),
    eraseMode: useAnnotationTool((s) => s.eraseMode),
    pressure: useAnnotationTool((s) => s.pressure),
    smoothing: useAnnotationTool((s) => s.smoothing),
    shapeFill: useAnnotationTool((s) => s.shapeFill),
    shapeDash: useAnnotationTool((s) => s.shapeDash),
    shapeCorner: useAnnotationTool((s) => s.shapeCorner),
  };
}

/** The style a NEW editor is constructed with, so an editor that mounts mid-session
 *  already carries the live tool instead of waiting for the next change. This is
 *  what makes it safe to mount and unmount editors as pages scroll. */
export function editorOpts(s: AnnotationStyle): EditorOpts {
  return {
    color: s.color,
    font: s.fontFamily,
    tool: s.tool,
    fontSizeRel: s.fontSizeRel,
    strokeRel: s.strokeRel,
    highlighterRel: s.highlighterRel,
    eraserRel: s.eraserRel,
    eraseMode: s.eraseMode,
    pressure: s.pressure,
    smoothing: s.smoothing,
    shapeFill: s.shapeFill,
    shapeDash: s.shapeDash,
    shapeCorner: s.shapeCorner,
  };
}

/**
 * Push the live tool and its style into every mounted editor, ONE FIELD PER EFFECT.
 *
 * This replaces one effect that listed 14 dependencies and called 11 setters on
 * every live editor whenever ANY of the 14 changed. Dragging a highlighter colour
 * therefore re-pushed the font, the pressure flag, the smoothing preset, the eraser
 * radius and the shape style into every page, and `setStrokeWidth` could re-derive a
 * pressure outline for a selected stroke off the back of an unrelated edit.
 *
 * With one effect per field, a change pushes exactly the setter it belongs to.
 * React runs effects in declaration order, so when several fields change together
 * they still arrive in the old order and `applyTool` still runs last.
 *
 * `collect` is read through a ref, so a caller may pass a fresh closure each render
 * without re-firing every effect.
 */
export function useEditorToolSync(
  collect: () => Iterable<PageEditor>,
  active: boolean,
  s: AnnotationStyle,
): void {
  const collectRef = useRef(collect);
  collectRef.current = collect;

  const each = useCallback(
    (fn: (ed: PageEditor) => void) => {
      for (const ed of collectRef.current()) fn(ed);
    },
    [],
  );

  useEffect(() => { if (active) each((ed) => ed.setColor(s.color)); }, [active, each, s.color]);
  useEffect(() => { if (active) each((ed) => ed.setFont(s.fontFamily)); }, [active, each, s.fontFamily]);
  useEffect(() => { if (active) each((ed) => ed.setFontSize(s.fontSizeRel)); }, [active, each, s.fontSizeRel]);
  useEffect(() => { if (active) each((ed) => ed.setStrokeWidth(s.strokeRel)); }, [active, each, s.strokeRel]);
  useEffect(() => { if (active) each((ed) => ed.setHighlighterWidth(s.highlighterRel)); }, [active, each, s.highlighterRel]);
  useEffect(() => { if (active) each((ed) => ed.setEraserRadius(s.eraserRel)); }, [active, each, s.eraserRel]);
  useEffect(() => { if (active) each((ed) => ed.setEraseMode(s.eraseMode)); }, [active, each, s.eraseMode]);
  useEffect(() => { if (active) each((ed) => ed.setPressure(s.pressure)); }, [active, each, s.pressure]);
  useEffect(() => { if (active) each((ed) => ed.setSmoothing(s.smoothing)); }, [active, each, s.smoothing]);
  useEffect(
    () => { if (active) each((ed) => ed.setShapeStyle(s.shapeFill, s.shapeDash, s.shapeCorner)); },
    [active, each, s.shapeFill, s.shapeDash, s.shapeCorner],
  );
  // Last, as it always was: applyTool reads the fields the setters above wrote.
  useEffect(() => { if (active) each((ed) => ed.applyTool(s.tool)); }, [active, each, s.tool]);
}
